import { makeAutoObservable, action, computed, toJS, extendObservable, set } from 'mobx';
import SettingsService from 'services/settingsService';
import RatingsService from 'services/ratingsService';
import FS from 'services/fileService';
import { isValidUrl } from 'helpers/validatorHelper';
import domainData from 'store/domainStore';
import tableOptions from 'pages/wines/config/config';
import { getInitialParams, saveParamsToStorage } from './common/filterUtils';

const STORAGE_KEY = 'wine_filter_params';

const defaultData = {
  name: '',
  serviceProviderId: '',
  producer: '',
  image: '',
  sweetness: '',
  color: '',
  style: '',
  countryId: null,
  region: '',
  isSuperVino: false,
  lookingForImporter: false,
  lookingForDistributor: false,
  year: '',
  typeId: null,
  subRegion: '',
  special: '',
  ratings: [],
  menuItemPrices: [],
  menuItemSpecs: [
    {
      image: '',
      title: 'Profile & Story',
      description: '',
      menuItemId: null,
      index: 0,
    },
    {
      image: '',
      title: 'Tasting Notes',
      description: '',
      menuItemId: null,
      index: 1,
    },
    {
      image: '',
      title: 'Pairing Recommendation',
      description: '',
      menuItemId: null,
      index: 2,
    },
  ],
};

// Default filter parameters
const defaultFilterParams = {
  params: {
    sort: {
      field: 'createdAt',
      order: 'desc',
    },
    filter: {},
    limit: 50,
    skip: 0,
    search: {
      value: '',
      fields: [],
    },
  },
};

const requiredFields = [
  'name',
  'serviceProviderId',
  'producer',
  'sweetness',
  'color',
  'style',
  'image',
  'typeId',
  'countryId',
  'region',
];

class Wines {
  getDefaultStoreProps = () => ({
    wines: {
      count: 0,
      data: [],
    },
    filterData: defaultFilterParams,
    wineState: '',
    wineData: JSON.parse(JSON.stringify(defaultData)),
    wineDataErrors: {},
    isCreateFormChanged: false,
    pageName: '',

    // sp list
    spList: [],

    ratings: [],
    ratingsLoaded: false,
    ratingsLoading: false,

    // countries data
    countries: [],
    countriesLoaded: false,
    countriesLoading: false,

    countriesSelected: null,
    wineTypesSelected: null,

    wineTypes: [],
    wineTypesLoaded: false,
    wineTypesLoading: false,

    defaultWineImage: null,
    defaultWineImageLoaded: false,
  });

  constructor() {
    makeAutoObservable(this);
    extendObservable(this, { ...this.getDefaultStoreProps() });
  }

  loadDefaultWineImage = async () => {
    if (this.defaultWineImage) return;

    const response = await FS.getDefaultWineImage();

    if (response?.data?.response_data) {
      this.defaultWineImage = response?.data?.response_data;
      this.setDefaultWineImage();
    }
  };

  @action
  reset() {
    set(this, this.getDefaultStoreProps());
  }

  @action
  setStoreWines(newData) {
    this.wines.data = [];
    newData.data.forEach(this.addStoreWine);
    this.wines.count = newData.count;
  }

  @action
  addStoreWine = element => {
    this.wines.data.push(element);
  };

  @action
  setFilter(filter) {
    this.filterData = filter;
    // Save to localStorage using common utility, but exclude search
    if (filter?.params) {
      const { search: _search, ...paramsWithoutSearch } = filter.params;
      const filterToSave = {
        ...filter,
        params: paramsWithoutSearch,
      };
      saveParamsToStorage(STORAGE_KEY, filterToSave);
    }
  }

  @action
  resetFilters() {
    this.setFilter(defaultFilterParams);
  }

  // Get current filter parameters using common utility with validation
  getCurrentFilterParams = (urlParams = {}) => {
    const isUserSuperAdmin = domainData.userIsSuperAdmin();
    const subscriptionAccess = domainData.getSubscriptionAllAccess();
    const userContext = { isUserSuperAdmin, subscriptionAccess };

    return getInitialParams(STORAGE_KEY, defaultFilterParams, tableOptions.wines, userContext, urlParams);
  };

  @computed
  get filter() {
    const filterToJS = toJS(this.filterData);
    return filterToJS;
  }

  @action
  createFormChanged = (value = true) => {
    this.isCreateFormChanged = value;
  };

  @computed
  getCreateFormChanged = () => this.isCreateFormChanged;

  getAllOptionsLoaded = () => this.wineTypesLoaded && this.countriesLoaded;

  @action
  clearTypeAutocompleteData = () => {
    this.wineTypesLoaded = false;
    this.wineTypesLoading = false;
  };

  getWineTypesData = async () => {
    if (this.wineTypesLoaded) {
      return this.wineTypes;
    }
    if (this.wineTypesLoading) {
      return new Promise(resolve => {
        const checkData = () => {
          if (this.wineTypesLoaded) {
            resolve(this.wineTypes);
          } else {
            setTimeout(checkData, 1000);
          }
        };
        checkData();
      });
    }
    await this.loadWineTypesData();
    return this.wineTypes;
  };

  loadWineTypesData = async () => {
    this.wineTypesLoading = true;
    try {
      const response = await SettingsService.getWineTypesOptions();
      if (response) {
        const items = [];
        response.data.forEach(wineType => {
          const item = {
            id: wineType.id,
            name: wineType.name,
          };
          if (item.id === this.wineData.typeId) {
            this.wineTypesSelected = item;
          }
          items.push(item);
        });
        this.wineTypes = items;
        this.wineTypesLoaded = true;
        this.wineTypesLoading = false;
      }
    } catch (err) {
      this.wineTypesLoading = false;
    }
  };

  @action
  setWineState(value) {
    this.wineState = value;
  }

  @action
  setDefaultWineImage = () => {
    if (this.defaultWineImage && !this.wineData.image) {
      this.wineData.image = this.defaultWineImage.url;
    }
  };

  @action
  initializeWineData = currentSP => {
    this.wineData = JSON.parse(JSON.stringify(defaultData));
    if (currentSP) {
      this.wineData.serviceProviderId = currentSP;
    }
  };

  getStoreWineForCreate = () => this.wineData;

  @computed
  get defaultWineImageUrl() {
    return this.defaultWineImage?.url || '';
  }

  @action
  setServiceProviderId = id => {
    if (this.wineState === 'create') {
      this.wineData = JSON.parse(JSON.stringify(defaultData));
      this.wineData.serviceProviderId = id;
    }
  };

  @action
  onChangeCheckBox = e => {
    this.createFormChanged();
    this.wineData[e.target.name] = e.target.checked;
  };

  @action
  onChangeButtonGroup = (name, value) => {
    this.createFormChanged();
    this.wineData[name] = value;
    if (!value && requiredFields.includes(name)) {
      this.wineDataErrors[name] = 'required';
    } else {
      delete this.wineDataErrors[name];
    }
  };

  handleFieldChange(event, type) {
    const { value, name } = event.target;
    if (name === 'countryId') {
      this.wineData.region = null;
    }
    this.wineData[name] = type === 'number' ? +value : value;

    if (!value && requiredFields.includes(name)) {
      this.wineDataErrors[name] = 'required';
    } else {
      delete this.wineDataErrors[name];
    }
  }

  @action
  onChangeUrlField = e => {
    const { name, value } = e.target;
    this.wineData[name] = value;

    if (!value && requiredFields.includes(name)) {
      this.wineDataErrors[name] = 'required';
    } else if (value && !isValidUrl(value)) {
      this.wineDataErrors[name] = 'invalidUrl';
    } else {
      delete this.wineDataErrors[name];
    }

    this.createFormChanged();
  };

  @action
  onChangeField = (e, type) => {
    this.createFormChanged();
    this.handleFieldChange(e, type);
    return true;
  };

  getWineData = isEdit => {
    const cleanData = JSON.parse(JSON.stringify(this.wineData));
    if (isEdit) {
      delete cleanData.active;
      delete cleanData.serviceProvider;
      delete cleanData.menuItemType;
    }
    const prices = [];
    cleanData.menuItemPrices.forEach(price => {
      if (price.unit && price.price) {
        prices.push({
          ...price,
          discountedPrice: price.discountedPrice || 0,
        });
      }
    });
    cleanData.menuItemPrices = prices;
    cleanData.ratings = cleanData.ratings.filter(rating => rating.ratingTypeId && rating.rating);

    return cleanData;
  };

  @action
  setWineData = data => {
    this.wineData = data;
    if (this.wineTypesLoaded) {
      this.wineTypesSelected = this.wineTypes.find(type => type.id === data.typeId);
    }
    if (this.countriesLoaded) {
      this.countriesSelected = this.countries.find(country => country.id === this.wineData.countryId);
    }
  };

  getWineDataErrors = () => this.wineDataErrors;

  @action
  clearWineDataErrors = () => {
    this.wineDataErrors = {};
    this.wineData = JSON.parse(JSON.stringify(defaultData));
    this.createFormChanged(false);
  };

  wineDataHasError = () => {
    let hasError = false;
    requiredFields.forEach(i => {
      if (i === 'typeId' && this.wineTypesSelected) {
        delete this.wineDataErrors[i];
      } else if (!this.wineData[i]) {
        this.wineDataErrors[i] = 'required';
        hasError = true;
      } else {
        delete this.wineDataErrors[i];
      }
    });
    if (this.wineDataErrors.whereToBuy) {
      hasError = true;
    }
    return hasError;
  };

  @action
  setPageName = value => {
    this.pageName = value;
  };

  @action
  setSpList = list => {
    this.spList = list;
  };

  @computed
  get getSpList() {
    return this.spList;
  }

  @computed
  get wineTypesOptions() {
    return this.wineTypes;
  }

  @computed
  get getSelectedWineType() {
    return this.wineTypesSelected;
  }

  @action
  handleAddPriceRow = () => {
    this.createFormChanged();
    this.wineData.menuItemPrices.push({
      unit: '',
      price: '',
      discountedPrice: '',
      menuItemId: this.wineData.id,
      index: this.wineData.menuItemPrices.length,
    });
  };

  @action
  onChangePriceUnit = (event, unit, optionIndex) => {
    this.createFormChanged();
    const { value, name } = event.target;
    const data = this.wineData.menuItemPrices.find((price, index) => index === optionIndex);
    if (data) {
      data[name] = value;
      // const changedData = this.wineData.menuItemPrices.find((price, index) => index === optionIndex);
      // if (!changedData.unit || !changedData.price || !changedData.discountedPrice) {
      //   this.wineDataErrors.price = 'required';
      // } else {
      //   delete this.wineDataErrors.price;
      // }
    }
    delete this.wineDataErrors.menuItemPrices;
    return true;
  };

  @action
  onChangeRating = (event, unit, optionIndex) => {
    this.createFormChanged();
    const { value, name } = event.target;
    const data = this.wineData.ratings.find((price, index) => index === optionIndex);
    if (data) {
      data[name] = value;
    }
    delete this.wineDataErrors.ratings;
    return true;
  };

  @action
  handleRemovePriceRow = index => {
    this.createFormChanged();
    this.wineData.menuItemPrices = this.wineData.menuItemPrices.filter((price, priceIndex) => priceIndex !== index);
    if (!this.wineData.menuItemPrices.length) {
      delete this.wineDataErrors.menuItemPrices;
    }
  };

  checkRatingsFieldsHasError = () => {
    let hasError = false;
    this.wineData.ratings.forEach(({ ratingTypeId, rating }) => {
      if (!ratingTypeId || !rating) {
        this.wineDataErrors.ratings = 'required';
        hasError = true;
      } else {
        delete this.wineDataErrors.ratings;
      }
    });
    return hasError;
  };

  checkPriceFieldsHasError = () => {
    let hasError = false;
    this.wineData.menuItemPrices.forEach(({ unit, price }) => {
      if (!unit || !price) {
        this.wineDataErrors.menuItemPrices = 'required';
        hasError = true;
      } else {
        delete this.wineDataErrors.menuItemPrices;
      }
    });
    return hasError;
  };

  checkWineSpecFieldsHasError = () => {
    let hasError = false;
    this.wineData.menuItemSpecs.forEach(spec => {
      if (!spec.title) {
        this.wineDataErrors.wineSpecs = 'required';
        hasError = true;
      }
    });
    return hasError;
  };

  @action
  onChangeWineSpec = (event, optionIndex, name) => {
    this.createFormChanged();
    if (event && typeof event === 'object') {
      const { value } = event.target;
      const data = this.wineData.menuItemSpecs.find((specItem, index) => index === optionIndex);
      if (data) {
        data[name] = value;
      }
    } else if (name === 'image') {
      const data = this.wineData.menuItemSpecs.find((specItem, index) => index === optionIndex);
      if (data) {
        data[name] = event;
      }
    }
    const changedData = this.wineData.menuItemSpecs.find((spec, index) => index === optionIndex);
    if (!changedData.title) {
      this.wineDataErrors.wineSpecs = 'required';
    } else {
      delete this.wineDataErrors.wineSpecs;
    }
    return true;
  };

  getRatingsData = async () => {
    if (this.ratingsLoaded) {
      return this.ratings;
    }
    if (this.ratingsLoading) {
      return new Promise(resolve => {
        const checkData = () => {
          if (this.ratingsLoaded) {
            resolve(this.ratings);
          } else {
            setTimeout(checkData, 1000);
          }
        };
        checkData();
      });
    }
    await this.loadRatingsData();
    return this.ratings;
  };

  loadRatingsData = async () => {
    this.ratingsLoading = true;
    try {
      const response = await RatingsService.getRatingsOptions();
      if (response) {
        this.ratings = response.data;
        this.ratingsLoaded = true;
        this.ratingsLoading = false;
      }
    } catch (err) {
      this.ratingsLoading = false;
    }
  };

  @computed
  get getRatingsOptions() {
    return this.ratings;
  }

  @action
  handleAddRatingRow = () => {
    this.createFormChanged();
    this.wineData.ratings.push({
      rating: 0,
      menuItemId: this.wineData.id,
      index: this.wineData.menuItemPrices.length,
    });
  };

  @action
  handleRemoveRatingRow = index => {
    this.createFormChanged();
    this.wineData.ratings = this.wineData.ratings.filter((rating, ratingIndex) => ratingIndex !== index);
    if (!this.wineData.ratings.length) {
      delete this.wineDataErrors.ratings;
    }
  };

  getCountriesData = async () => {
    if (this.countriesLoaded) {
      return this.countries;
    }
    if (this.countriesLoading) {
      return new Promise(resolve => {
        const checkData = () => {
          if (this.countriesLoaded) {
            resolve(this.countries);
          } else {
            setTimeout(checkData, 1000);
          }
        };
        checkData();
      });
    }
    await this.loadCountriesData();
    return this.countries;
  };

  loadCountriesData = async () => {
    this.countriesLoading = true;
    try {
      const response = await SettingsService.getCountriesOptions();
      if (response) {
        const countriesData = [];
        response.data.forEach(country => {
          const item = {
            id: country.id,
            name: country.name,
          };
          if (item.id === this.wineData.countryId) {
            this.countriesSelected = item;
          }
          countriesData.push(item);
        });
        this.countries = countriesData;
        this.countriesLoaded = true;
        this.countriesLoading = false;
      }
    } catch (err) {
      this.countriesLoading = false;
    }
  };

  @computed
  get isCountryOptionLoaded() {
    return this.countriesLoaded;
  }

  @computed
  get countriesOptions() {
    return this.countries;
  }

  @computed
  get getSelectedOption() {
    return this.countriesSelected;
  }

  @action
  addNewAutocompleteDataTmp = (text, name, addType) => {
    this.isSpChanged = false;
    if (!text) return;
    const existItem = this.wineData[addType].find(i => i.name === text);
    if (!existItem) {
      const newCategory = { id: 'newId', name: text, serviceProviderId: this.wineData.serviceProviderId };
      this[`${addType}Selected`] = newCategory;
      this.wineData[addType] = [...this.wineData[addType], newCategory];
      delete this.wineDataErrors[name];
    }
  };

  onChangeAutocomplete = (newValue, name, addType) => {
    this.createFormChanged();
    if (newValue && typeof newValue === 'object') {
      this[`${addType}Selected`] = newValue;
      this.wineData[name] = newValue.id;
      delete this.wineDataErrors[name];
    } else if (!newValue && requiredFields.includes(name)) {
      this.wineDataErrors[name] = 'required';
      this.wineData[name] = null;
      this[`${addType}Selected`] = null;
    }
  };

  setProducerDefaultValue = value => {
    this.wineData.producer = value;
  };
}

export default new Wines();
